<template>
    <div class="expenses-tab">
        <!-- Header -->
        <div class="tab-header">
            <div class="tab-header-left">
                <h3 class="tab-title">Expenses</h3>
            </div>
            <themed-button 
                v-if="leases.filtered.length"
                @click="openManageExpenses" 
                variant="default"
            >
                <span>Manage Expenses</span>
            </themed-button>
        </div>

        <!-- Expenses Table and Pie Chart -->
        <div class="expenses-tab__section expenses-tab__main-layout">
            <div class="expenses-tab__table-container">
                <themed-table
                    v-if="expenses.length"
                    :data="expenses"
                    :columns="expensesColumns"
                    :hoverable="true"
                    aria-label="Expenses table"
                    @row-click="openExpenseModal"
                />
                <div v-else class="expenses-tab__empty">No Expenses Found</div>
            </div>

            <!-- Pie Chart -->
            <div v-if="expenses.length" class="expenses-tab__chart-container">
                <h4 class="expenses-tab__chart-title">PSF Breakdown</h4>
                <div class="expenses-tab__chart">
                    <theme-pie-chart
                        :data="psfChartData"
                        :show-percentages="true"
                    />
                </div>
            </div>
        </div>

        <!-- Expense Timeline -->
        <div v-if="costByYear" class="expenses-tab__timeline">
            <div class="expenses-tab__timeline-header">
                <h4 class="expenses-tab__timeline-title">Expense Timeline</h4>
                <div class="expenses-tab__timeline-controls">
                    <select v-model="selectedLeaseId" class="expenses-tab__select">
                        <option v-for="lease in leases.all" :value="lease.id" :key="lease.id">{{ lease.name }}</option>
                    </select>
                    <select v-model="costType" class="expenses-tab__select">
                        <option value="cost_by_term_year">By Term Year</option>
                        <option value="cost_by_calendar_year">By Calendar Year</option>
                    </select>
                    <select v-if="!fromReatlas" v-model="selectedYear" class="expenses-tab__select">
                        <option value="">Year</option>
                        <option v-for="(data, year) in costByYear" :value="year" :key="year">{{ year }}</option>
                    </select>
                    <a :href="'/sites/' + siteId + '/export/expenses/timeline'" class="expenses-tab__export-link">
                        <theme-icon icon="download-icon" type="icons" classes="w-5 h-5" />
                    </a>
                </div>
            </div>

            <div class="expenses-tab__timeline-layout">
                <!-- Timeline Table -->
                <div class="expenses-tab__timeline-table">
                    <themed-table
                        v-if="selectedLease"
                        :data="timelineData"
                        :columns="timelineColumns"
                        :hoverable="true"
                        aria-label="Expense timeline table"
                    >
                        <template #footer>
                            <tr class="expenses-tab__table-row--totals">
                                <td :colspan="costType == 'cost_by_term_year' ? 4 : 2">Totals:</td>
                                <td data-precision="0" :data-originalvalue="selectedLease.total_cost" class="is-currency">
                                    {{ formatCurrency(selectedLease.total_cost / 100, 0) }}
                                </td>
                                <td data-precision="0" :data-originalvalue="selectedLease.total_remaining" class="is-currency">
                                    {{ formatCurrency(selectedLease.total_remaining / 100, 0) }}
                                </td>
                            </tr>
                        </template>
                    </themed-table>
                </div>

                <!-- Line Chart -->
                <div class="expenses-tab__timeline-chart">
                    <h4 class="expenses-tab__chart-title">Total vs Remaining</h4>
                    <div class="expenses-tab__chart">
                        <theme-line-chart
                            v-if="timelineChartData && timelineChartData.categories.length > 0"
                            :key="`timeline-chart-${selectedLeaseId}-${costType}`"
                            :categories="timelineChartData.categories"
                            :datasets="timelineChartData.datasets"
                            :format-value="formatChartCurrency"
                        />
                        <div v-else class="expenses-tab__empty">
                            No timeline data available
                        </div>
                    </div>
                </div>
            </div>

            <!-- Year Detail Section -->
            <div v-if="!fromReatlas" class="expenses-tab__year-detail">
                <themed-table
                    v-if="selectedYear != ''"
                    :data="expensesFiltered"
                    :columns="yearDetailColumns"
                    :hoverable="true"
                    aria-label="Year detail expenses table"
                />
                <div v-else class="expenses-tab__select-prompt">
                    <button @click="selectedYear = Object.keys(costByYear)[0]" class="expenses-tab__select-button">
                        Select a Year
                    </button>
                </div>
                <div v-if="expensesFiltered.length == 0 && selectedYear != ''" class="expenses-tab__empty">
                    No Expenses Found
                </div>
            </div>
        </div>

        <!-- Modals -->
        <modal
            name="expense-modal"
            :min-width="1072"
            :max-width="1072"
            height="auto"
            click-to-close
            scrollable
            adaptive
            draggable=".expense-title-bar"
        >
            <expense-modal :expense-data="selectedExpense" />
        </modal>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import moment from 'moment';
    import ThemePieChart from '../charts/ThemePieChart.vue';
    import ThemeLineChart from '../charts/ThemeLineChart.vue';
    
    export default {
        name: 'ThemedExpenses',
        
        components: {
            ThemePieChart,
            ThemeLineChart
        },

        props: {
            data: {
                type: [Object, Array],
                required: true
            },

            siteId: {
                type: Number,
                required: false
            },

            fromReatlas: {
                type: Boolean,
                default: false
            }
        },

        data() {
            return {
                selectedLeaseId: this.data[0]?.id || null,
                costType: 'cost_by_term_year',
                selectedYear: '',
                selectedMonth: '',
                selectedExpense: null,
                isDarkMode: document.documentElement.classList.contains('dark'),
                months: [
                    { label: 'Jan', value: '1' },
                    { label: 'Feb', value: '2' },
                    { label: 'Mar', value: '3' },
                    { label: 'Apr', value: '4' },
                    { label: 'May', value: '5' },
                    { label: 'Jun', value: '6' },
                    { label: 'Jul', value: '7' },
                    { label: 'Aug', value: '8' },
                    { label: 'Sep', value: '9' },
                    { label: 'Oct', value: '10' },
                    { label: 'Nov', value: '11' },
                    { label: 'Dec', value: '12' },
                ]
            }
        },

        mounted() {
            this.$eventHub.$on('leaseSaved', () => {
                this.$eventHub.$emit('convert-currency');
            });

            this.$eventHub.$on('reloadSite', async () => {
                await this.$store.dispatch('loadSite', this.siteId);
                
                this.leases.all = this.sites.selectedSite.leases;
                this.leases.filtered = this.sites.selectedSite.leases;
            });

            this.$eventHub.$emit('convert-currency');

            // Watch for theme changes
            this.themeObserver = new MutationObserver(() => {
                this.isDarkMode = document.documentElement.classList.contains('dark');
            });
            
            this.themeObserver.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['class']
            });
        },

        beforeDestroy() {
            if (this.themeObserver) {
                this.themeObserver.disconnect();
            }
        },

        watch: {
            selectedLease() {
                this.$eventHub.$emit('convert-currency');
            },

            selectedMonth() {
                this.$eventHub.$emit('convert-currency');
            },

            selectedYear() {
                this.$eventHub.$emit('convert-currency');
            }
        },

        computed: {
            ...mapState(['leases']),
            ...mapState(['sites']),

            expensesColumns() {
                const cols = [
                    { field: 'lease_name', label: 'Lease', sortable: false },
                    { field: 'type.name', label: 'Type', sortable: false, formatter: (value, row) => row.type?.name },
                    { field: 'start_date', label: 'Start', sortable: false, align: 'center',
                    width:"90px", formatter: (value) => this.$options.filters.date(value), class: 'expenses-tab__table-cell--desktop' },
                    { field: 'end_date', label: 'End', sortable: false, align: 'center',  width:"90px", formatter: (value) => this.$options.filters.date(value), class: 'expenses-tab__table-cell--desktop' },
                    { 
                        field: 'amount', 
                        label: 'Base', 
                        sortable: false,
                        align: 'right',
                        formatter: (value, row) => this.formatCurrency(value / 100, row.frequency == 'PSF' ? 2 : 0)
                    },
                    { field: 'frequency', label: 'Frequency', sortable: false }
                ];

                if (!this.fromReatlas) {
                    cols.push(
                        { field: 'amount_psf', label: 'PSF', sortable: false, align: 'right', formatter: (value) => this.formatCurrency(value / 100, 2) },
                        { field: 'amount_monthly', label: 'Monthly', sortable: false, align: 'right', formatter: (value) => this.formatCurrency(value / 100, 0), class: 'expenses-tab__table-cell--desktop' },
                        { field: 'amount_annually', label: 'Annual', sortable: false, align: 'right', formatter: (value) => this.formatCurrency(value / 100, 0), class: 'expenses-tab__table-cell--desktop' },
                        { 
                            field: 'is_liability', 
                            label: 'Liability?', 
                            sortable: false,
                             width:"60px",
                            formatter: (value) => value == null ? '-' : (value ? 'Yes' : 'No'),
                            class: 'expenses-tab__table-cell--desktop expenses-tab__table-cell--center'
                        }
                    );
                }

                return cols;
            },

            timelineColumns() {
                const cols = [
                    { field: 'year', label: 'Year', sortable: false }
                ];

                if (this.costType == 'cost_by_term_year') {
                    cols.push(
                        { field: 'period_start', label: 'Start', sortable: false, align: 'center', formatter: (value) => this.$options.filters.date(value) },
                        { field: 'period_end', label: 'End', sortable: false, align: 'center', formatter: (value) => this.$options.filters.date(value) }
                    );
                }

                cols.push(
                    { field: 'psf', label: 'PSF', sortable: false, align: 'right', formatter: (value) => this.formatCurrency(value / 100, 2) },
                    { field: 'total', label: 'Total', sortable: false, align: 'right', formatter: (value) => this.formatCurrency(value / 100, 0) },
                    { field: 'remaining', label: 'Remaining', sortable: false, align: 'right', formatter: (value) => this.formatCurrency(value / 100, 0) }
                );

                return cols;
            },

            timelineData() {
                if (!this.costByYear) return [];
                
                return Object.keys(this.costByYear).map(year => ({
                    year: year,
                    ...this.costByYear[year]
                }));
            },

            yearDetailColumns() {
                return [
                    { field: 'type.name', label: 'Type', sortable: false, formatter: (value, row) => row.type?.name },
                    { 
                        field: 'current_cost', 
                        label: 'Current Cost', 
                        sortable: false,
                        align: 'right',
                        formatter: (value) => value
                    },
                    { field: 'frequency', label: 'Frequency', sortable: false },
                    { field: 'annual_cost', label: 'Annual', sortable: false, align: 'right', formatter: (value) => value }
                ];
            },

            expenses() {
                let leases = _.filter(this.leases.all, lease => lease.id != 'new');

                return _.flatMap(leases, lease => {
                    return (lease.expenses || []).map(expense => {
                        expense = { ...expense, lease_name: lease.name };

                        if (typeof expense.cost_by_calendar_year === 'string' && expense.cost_by_calendar_year.trim() !== '') {
                            try {
                                expense.cost_by_calendar_year = JSON.parse(expense.cost_by_calendar_year);
                            } catch (e) {
                                expense.cost_by_calendar_year = {};
                            }
                        }

                        if (typeof expense.cost_by_term_year === 'string' && expense.cost_by_term_year.trim() !== '') {
                            try {
                                expense.cost_by_term_year = JSON.parse(expense.cost_by_term_year);
                            } catch (e) {
                                expense.cost_by_term_year = {};
                            }
                        }

                        return expense;
                    });
                });
            },

            expensesFiltered() {
                if (this.selectedYear == "") {
                    return [];
                }

                return _.filter(this.expenses, (expense) => {
                    if (this.costType == 'cost_by_calendar_year') { 
                        const start = moment(expense.start_date);

                        expense.current_cost = this.calculateCurrentCost(expense);
                        expense.annual_cost = this.calculateCurrentAnnualCost(expense)

                        return expense.expensable_id == this.selectedLeaseId
                                && (moment(this.selectedYear + "-01-01").isBetween(expense.start_date, expense.end_date, 'year', '[]')
                                    && this.selectedMonth == ""
                                        ? true
                                        : moment(this.selectedYear + "-" + this.selectedMonth + "-" + start.format("DD")).isBetween(expense.start_date, expense.end_date, 'month', '[]'));
                   
                    } else if (this.costType == 'cost_by_term_year') { 
                        if (typeof this.costByYear[this.selectedYear] == "undefined") {
                            return;
                        }

                        const period_start = this.costByYear[this.selectedYear].period_start;
                        const period_end = this.costByYear[this.selectedYear].period_end;

                        expense.current_cost = this.calculateCurrentCost(expense);
                        expense.annual_cost = this.calculateCurrentAnnualCost(expense)

                        return expense.expensable_id == this.selectedLeaseId
                            && moment(expense.start_date).isSameOrBefore(period_start, 'year')
                            && (moment(expense.end_date).isSameOrAfter(period_end, 'day')
                                || moment(expense.end_date).isBetween(period_start, period_end, 'day', '[]'));
                    }
                });
            },

            selectedLease() {
                return _.find(this.leases.all, ['id', this.selectedLeaseId]);
            },

            costByYear() {
                if (!this.selectedLease) return null;
                
                const data = this.selectedLease[this.costType];
                
                // If it's a Laravel collection or array, convert to plain object
                if (Array.isArray(data)) {
                    return data.reduce((acc, item, index) => {
                        acc[index] = item;
                        return acc;
                    }, {});
                }
                
                // If it's already an object, return as is
                return data && typeof data === 'object' ? data : null;
            },

            psfChartData() {
                // Group expenses by type and calculate PSF totals
                const psfByType = {};
                
                this.expenses.forEach(expense => {
                    const typeName = expense.type?.name || 'Other';
                    if (!psfByType[typeName]) {
                        psfByType[typeName] = 0;
                    }
                    psfByType[typeName] += parseFloat(expense.amount_psf || 0) / 100;
                });

                return Object.keys(psfByType).map(type => ({
                    label: type,
                    value: parseFloat(psfByType[type].toFixed(2))
                }));
            },

            timelineChartData() {
                if (!this.costByYear || Object.keys(this.costByYear).length === 0) {
                    return null;
                }

                const categories = [];
                const totalData = [];
                const remainingData = [];

                Object.keys(this.costByYear).forEach(year => {
                    const data = this.costByYear[year];
                    categories.push(`Year ${year}`);
                    // Convert cents to dollars
                    const total = parseFloat(data.total || 0) / 100;
                    const remaining = parseFloat(data.remaining || 0) / 100;
                    totalData.push(total);
                    remainingData.push(remaining);
                });

                // Only return data if we have valid values
                if (categories.length === 0 || (totalData.every(v => v === 0) && remainingData.every(v => v === 0))) {
                    return null;
                }

                return {
                    categories,
                    datasets: [
                        {
                            label: 'Total',
                            data: totalData
                        },
                        {
                            label: 'Remaining',
                            data: remainingData
                        }
                    ]
                };
            }
        },

        methods: {
            formatCurrency(value, decimals = 0) {
                return '$' + parseFloat(value).toLocaleString('en-US', {
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals
                });
            },

            formatChartCurrency(value) {
                return '$' + parseFloat(value).toLocaleString('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                });
            },

            openManageExpenses() {
                this.$emit('open-manage-leases', 'expenses');
            },

            openExpenseModal(expense) {
                this.selectedExpense = expense;
                this.$modal.show('expense-modal');
            },

            calculateCurrentCost(expense) {
                let annualCost = this.calculateCurrentAnnualCost(expense);

                switch(expense.frequency) {
                    case 'PSF':
                        return this.selectedLease.size ? annualCost / this.selectedLease.size : 0;

                    case 'Monthly':
                        return annualCost / 12;

                    case 'Quarterly':
                        return annualCost / 4;

                    case 'Annual':
                    case 'One Time':
                        return annualCost;

                    case 'Semi-Annual':
                        return annualCost / 2;
                }
            },

            calculateCurrentAnnualCost(expense) {
                if (this.costType == 'cost_by_term_year') {
                    return expense.cost_by_term_year[this.selectedYear].total;
                } else if (this.costType == 'cost_by_calendar_year') {
                    if (this.selectedYear != "" && this.selectedMonth == "") {
                        return expense.cost_by_calendar_year[this.selectedYear]?.total || 0;
                    } else if (this.selectedYear != "" && this.selectedMonth != "") {
                        return expense.cost_by_calendar_year[this.selectedYear]['by_month'][this.selectedMonth];
                    }

                    return 0;
                }
            }
        }
    }
</script>
